import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LockKeyhole } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@cartcloser/ui';
import { createAgent, listAgents, listTemplates } from '../lib/api.js';
import { useMe } from '../components/dashboard-layout.js';

export const Route = createFileRoute('/admin/$slug/agents/')({
  component: AgentsPage,
});

function AgentsPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useMe();
  const agentsQuery = useQuery({ queryKey: ['agents', slug], queryFn: () => listAgents(slug) });
  const templatesQuery = useQuery({
    queryKey: ['templates', slug],
    queryFn: () => listTemplates(slug),
  });

  const [expertise, setExpertise] = useState('');
  const [templateId, setTemplateId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createAgent(slug, {
        expertise,
        templateId: templateId || null,
        isUnlinked: templateId === '',
      }),
    onSuccess: async ({ id }) => {
      await queryClient.invalidateQueries({ queryKey: ['agents', slug] });
      await navigate({ to: '/admin/$slug/agents/$agentId', params: { slug, agentId: id } });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to create agent'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Agents</h1>
        <p className="text-sm text-muted-foreground">
          Each agent has a short expertise the Receptionist routes by. Base it on a template, then
          tune the prompts.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New agent</CardTitle>
          <CardDescription>Pick a template (or start blank) and name its expertise.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              create.mutate();
            }}
          >
            <FormField label="Expertise" className="flex-1" required>
              {({ id }) => (
                <Input
                  id={id}
                  value={expertise}
                  onChange={(e) => setExpertise(e.target.value)}
                  placeholder="Dishwashers"
                  required
                />
              )}
            </FormField>
            <FormField label="Template" className="flex-1">
              {({ id }) => (
                <select
                  id={id}
                  value={templateId}
                  onChange={(e) => {
                    setTemplateId(e.target.value);
                    const t = templatesQuery.data?.find((x) => x.id === e.target.value);
                    if (t && !expertise) setExpertise(t.defaultExpertise);
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Blank (unlinked)</option>
                  {templatesQuery.data?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
            <Button type="submit" loading={create.isPending}>
              Create
            </Button>
          </form>
          {error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {agentsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : agentsQuery.data && agentsQuery.data.length > 0 ? (
          agentsQuery.data.map((a) => (
            <Card key={a.id} className="transition-colors hover:border-primary/40">
              <CardContent className="flex items-center justify-between gap-3 p-4">
                <Link
                  to="/admin/$slug/agents/$agentId"
                  params={{ slug, agentId: a.id }}
                  className="min-w-0 flex-1"
                >
                  <p className="font-medium">{a.expertise}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.isUnlinked ? 'Unlinked' : (a.templateName ?? 'Template')} ·{' '}
                    {a.skuScopeMode === 'whitelist' ? 'SKU whitelist' : 'Categories'}
                  </p>
                </Link>
                <div className="flex shrink-0 items-center gap-3">
                  <Badge variant={a.enabled ? 'success' : 'secondary'}>
                    {a.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <Link
                    to="/admin/$slug/sandbox"
                    search={{ agent: a.id }}
                    params={{ slug }}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Test
                  </Link>
                  {meQuery.data?.user.isSuperAdmin && a.templateId ? (
                    <Link
                      to="/super/templates/$id"
                      params={{ id: a.templateId }}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      <LockKeyhole className="size-3.5" aria-hidden="true" />
                      Edit template
                    </Link>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">No agents yet. Create your first above.</p>
        )}
      </div>
    </div>
  );
}
